import type { BrowseState, HistoryMode, SortKey } from './browse.ts';

/** The Collection page's URL search params (collection-browsing spec, View state in the URL). */
export type CollectionSearch = BrowseState & {
  /** Current Collection id. */
  c?: string;
  view?: 'grid' | 'table';
  /** Selected Copy id (detail view). */
  copy?: string;
};

const SORTS: SortKey[] = ['added', 'artist', 'title', 'year', 'acquired', 'location'];
const HISTORY: HistoryMode[] = ['hide', 'show', 'only'];
const STATUSES = ['owned', 'sold', 'given_away', 'lost'] as const;
const LISTS = ['format', 'genre', 'decade', 'country', 'tag', 'household', 'location'] as const;

const str = (v: unknown, max = 200) => (typeof v === 'string' && v.length > 0 && v.length <= max ? v : undefined);
const list = (v: unknown) => {
  const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
  const clean = arr.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 100).slice(0, 50);
  return clean.length ? clean : undefined;
};
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]) => (allowed.includes(v as T) ? (v as T) : undefined);

/** Reads search params, dropping anything unknown or invalid. */
export function validateCollectionSearch(raw: Record<string, unknown>): CollectionSearch {
  const out: CollectionSearch = {};
  const set = <K extends keyof CollectionSearch>(k: K, v: CollectionSearch[K] | undefined) => {
    if (v !== undefined) out[k] = v;
  };
  set('c', str(raw.c, 40));
  set('q', str(raw.q));
  set('view', oneOf(raw.view, ['grid', 'table'] as const));
  set('sort', oneOf(raw.sort, SORTS));
  set('dir', oneOf(raw.dir, ['asc', 'desc'] as const));
  for (const key of LISTS) set(key, list(raw[key]));
  set('history', oneOf(raw.history, HISTORY));
  set('status', oneOf(raw.status, STATUSES));
  set('copy', str(raw.copy, 40));
  return out;
}

const VIEW_KEY = 'vinyl.view';
const COLUMNS_KEY = 'vinyl.columns';

/** Grid or table is remembered per device; storage may be unavailable. */
export function savedView(): 'grid' | 'table' | null {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === 'grid' || v === 'table' ? v : null;
  } catch {
    return null;
  }
}

export function saveView(view: 'grid' | 'table'): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Not stored; the default applies next time.
  }
}

export function savedColumns(fallback: string[]): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? 'null') as unknown;
    return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : fallback;
  } catch {
    return fallback;
  }
}

export function saveColumns(columns: string[]): void {
  try {
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(columns));
  } catch {
    // ignore
  }
}
